import { Grid } from '../../../../Common/Grid'
import { Button, Input } from '@fullscreendigital/carrefour-dex-fe'
import { TitleHeader } from '../../../Common/TitleHeader'
import { useLocation, useNavigate } from 'react-router-dom'
import { getValidationSchema, getInitialValues } from './helpers'
import { useFormik } from 'formik'
import { useEffect, useMemo, useState } from 'react'
import { formatErrorMessage } from '../../../../../../utils/formatting'
import { useNotification } from '../../../../../../hooks'
import { useQuery } from 'react-query'
import { makeCoreGetRequest, makeCorePostRequest } from '../../../../../../utils/api'
import ImageUpload from '../../../Common/ImageUpload/ImageUpload'
import AlertModal from '../../../Common/AlertModal/AlertModal'
import { URLS } from '../../../constants'
import {
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material'
import { useOfflineElementTool } from 'components/core-ui/Offline'

const SupportScanStep3 = () => {
  const { showNotification } = useNotification()
  const navigate = useNavigate()
  const location = useLocation()
  const { supportId, truckId } = location.state || ''

  const [isMissingValuesModalOpen, setIsMissingValuesModalOpen] = useState(false)
  const [MissingValuesModalContent, setMissingValuesModalContent] = useState({
    title: '',
    description: '',
    buttons: []
  })
  const [isDifferentNumberModalOpen, setIsDifferentNumberModalOpen] = useState(false)
  const [DifferentNumberModalContent, setDifferentNumberModalContent] = useState({
    title: '',
    description: '',
    buttons: []
  })
  const [rows, setRows] = useState([])

  const { offlineData, deleteOfflineData, saveOfflineData } = useOfflineElementTool({
    dataType: 'SUPPORT_SCAN_STEP_3',
    url: URLS.AUTH_SUPPORT_SCAN_STEP_3,
    elementId: `${truckId}-${supportId}`
  })

  const handleError = error => {
    showNotification({
      message: `${formatErrorMessage(error)}`,
      type: 'error'
    })
  }

  const { data: supportTypeList, isLoading } = useQuery(
    'SupportPrincipalTypeId',
    async () => {
      const support = await makeCoreGetRequest(
        `/shop-reception-mobile/support/shop/secondary/type?supportId=${supportId}`,
        null
      )
      return support
    },
    {
      initialData: [],
      onError: handleError
    }
  )

  const validationSchema = useMemo(() => getValidationSchema(), [])

  const initialValues = useMemo(
    () => getInitialValues(supportTypeList, offlineData),
    [supportTypeList, offlineData]
  )

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    initialValues: initialValues,
    validationSchema: validationSchema,
    onSubmit: async (values: any) => {
      let missingValue = false
      const message = 'Există diferențe între cantitatea aşteptată şi cea descărcată pentru '
      let supportsNotOk = ''
      let uploaded: boolean[] = []

      values.supportType.forEach(support => {
        if (support.received === null) missingValue = true

        const total = Number(support.received)
        supportTypeList.forEach(st => {
          if (st.id === support.id && st.received !== total) {
            supportsNotOk = supportsNotOk + st.name + ', '
            if (support.imageUploaded === false) uploaded.push(false)
          }
        })
      })

      if (missingValue) {
        openMissingValuesModal()
      } else if (supportsNotOk.length > 0) {
        showDifferentNumberConfirmation(values.supportType, message + supportsNotOk, uploaded)
      } else {
        await doSaveSupportTypes(values.supportType)
      }
    }
  })

  if (isLoading) {
    return <CircularProgress color="primary" style={{ margin: '40px auto' }} disableShrink />
  }

  const handleSuccess = async id => {
    formik.setFieldValue(`supportType[${id}].imageUploaded`, true)

    await saveOfflineData([{ field: `supportType[${id}].imageUploaded`, value: true }])

    showNotification({
      message: 'Imaginea a fost încărcată cu succes.',
      type: 'success'
    })
  }

  useEffect(() => {
    setRows(
      formik.values?.supportType.map((support, index) => ({
        id: index,
        name: support.name,
        received: support.received
      }))
    )
  }, [formik.values?.supportType])

  const isSubmitButtonDisabled = useMemo(
    () =>
      formik.values?.supportType.some((support, index) => {
        return (
          support.received !== supportTypeList[index]?.received &&
          support.received &&
          !support.imageUploaded
        )
      }),
    [formik.values?.supportType, supportTypeList]
  )

  const openMissingValuesModal = () => {
    setMissingValuesModalContent({
      title: 'Atenție!',
      description: 'Vă rugăm să completaţi toate valorile din ecran!',
      buttons: [
        {
          label: 'Ok',
          onClick: () => setIsMissingValuesModalOpen(false),
          color: 'primary'
        }
      ]
    })
    setIsMissingValuesModalOpen(true)
  }

  const closeMissingValuesModal = () => {
    setIsMissingValuesModalOpen(false)
  }

  const showDifferentNumberConfirmation = (values, message, imageUploaded) => {
    const buttons = [
      {
        label: 'Mai verific',
        onClick: () => setIsDifferentNumberModalOpen(false),
        color: 'primary'
      }
    ]

    if (!imageUploaded || imageUploaded.length === 0) {
      buttons.push({
        label: 'Am terminat',
        onClick: async () => {
          setIsDifferentNumberModalOpen(false)
          await doSaveSupportTypes(values)
        },
        color: 'primary'
      })
    }

    setDifferentNumberModalContent({
      title: message,
      description: '',
      buttons: buttons
    })
    setIsDifferentNumberModalOpen(true)
  }

  const closeDifferentNumberConfirmation = () => {
    setIsDifferentNumberModalOpen(false)
  }

  const doSaveSupportTypes = async values => {
    try {
      await makeCorePostRequest(
        `/shop-reception-mobile/support/shop/save/list/${false}/${truckId}/${supportId}`,
        values
      )
      await deleteOfflineData()
      navigate(URLS.AUTH_SUPPORT_SCAN_STEP_1, { state: truckId })
    } catch (error) {
      showNotification({
        message: `Error while submitting. ${formatErrorMessage(error)}`,
        type: 'error'
      })
    }
  }

  return (
    <Grid
      gridContainerStyle={{ marginTop: '20px' }}
      maxWidth="xs"
      items={[
        <TitleHeader title="Cod Suport" />,
        <TableContainer component={Paper}>
          <Table aria-label="support types table">
            <TableHead style={{ backgroundColor: '#0970E6' }}>
              <TableRow>
                <TableCell style={{ color: 'white' }}>TIP AMBALAJ</TableCell>
                <TableCell align="center" style={{ color: 'white' }}>
                  CANTITATE
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(rowData => (
                <>
                  <TableRow key={rowData.id}>
                    <TableCell component="th" scope="row">
                      {rowData.name}
                    </TableCell>
                    <TableCell align="center">
                      <Input
                        type="number"
                        value={rowData.received}
                        onChange={e => {
                          Number((e.target as HTMLInputElement).value) >= 0 &&
                          formik.values.supportType.map(async (support, idx) => {
                            if (rowData.id === idx) {
                              formik.setFieldValue(
                                `supportType[${idx}].received`,
                                Number((e.target as HTMLInputElement).value)
                              )
                              await saveOfflineData([
                                {
                                  field: `supportType[${idx}].received`,
                                  value: Number((e.target as HTMLInputElement).value)
                                }
                              ])
                            }
                          })
                        }}
                        variant="standard"
                      />
                    </TableCell>
                  </TableRow>
                  {supportTypeList[rowData.id].received !=
                    formik.values.supportType[rowData.id].received && (
                      <TableRow>
                        <TableCell colSpan={2}>
                          <ImageUpload
                            uploadUrl={`/shop-reception-mobile/support/shop/upload/` + rowData.id}
                            onSuccess={() => handleSuccess(rowData.id)}
                            onError={handleError}
                            btnStyle={{
                              color: formik.values.supportType[rowData.id].imageUploaded
                                ? 'green'
                                : '#0970E6'
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    )
                  }
                </>
              ))}
            </TableBody>
          </Table>
        </TableContainer>,
        <Button
          style={{ width: '100%' }}
          type="button"
          variant="contained"
          label="CONFIRMĂ AMBALAJE"
          onClick={() => formik.handleSubmit()}
          disabled={isSubmitButtonDisabled || formik.isSubmitting}
        />,
        <AlertModal
          open={isMissingValuesModalOpen}
          handleClose={closeMissingValuesModal}
          title={MissingValuesModalContent.title}
          description={MissingValuesModalContent.description}
          buttons={MissingValuesModalContent.buttons}
        />,
        <AlertModal
          open={isDifferentNumberModalOpen}
          handleClose={closeDifferentNumberConfirmation}
          title={DifferentNumberModalContent.title}
          description={DifferentNumberModalContent.description}
          buttons={DifferentNumberModalContent.buttons}
        />
      ]}
      itemsPerRow={1}
    />
  )
}

export default SupportScanStep3
